/*
 * Staff logins, for home managers and the pharmacy.
 *
 * Setting someone up produces a one-off password, shown once. They choose their own the first
 * time they sign in. Nobody, including a manager, can see anyone else's password afterwards.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from './api.ts';

export type Staff = {
  id: string;
  name: string;
  initials: string;
  jobTitle: string;
  email: string;
  roles: string[];
  agency: boolean;
  shiftEndsAt: string | null;
  competencyOn: string | null;
  active: boolean;
  mustSetPassword: boolean;
  locked: boolean;
  lastLoginAt: string | null;
};

const ROLES: Array<{ id: string; label: string; help: string }> = [
  { id: 'meds', label: 'Medicines round', help: 'Record medicines as given or not given' },
  { id: 'creams', label: 'Creams only', help: 'Apply creams and patches, no tablets' },
  { id: 'check', label: 'Second check', help: 'Check medicine changes and witness controlled drugs' },
  { id: 'nurse', label: 'Nurse', help: 'Insulin, injections and PEG tube medicines' },
  { id: 'manage', label: 'Manager', help: 'Reports, staff logins and settings' },
  { id: 'view', label: 'View records', help: 'Read the MAR chart and records' },
];

export function StaffScreen({ homeId, myId }: { homeId: string; myId: string }) {
  const [staff, setStaff] = useState<Staff[] | null>(null);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [newPassword, setNewPassword] = useState<{ name: string; password: string; note: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ staff: Staff[] }>(`/api/homes/${homeId}/staff`);
      setStaff(data.staff);
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the staff list.');
    }
  }, [homeId]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = useCallback(
    async (person: Staff, what: 'reset' | 'unlock' | 'off' | 'on') => {
      try {
        if (what === 'reset') {
          if (!confirm(`Give ${person.name} a new password? They will be signed out everywhere.`)) return;
          const data = await api.post<{ password: string; message: string }>(`/api/staff/${person.id}/password-reset`);
          setNewPassword({ name: person.name, password: data.password, note: data.message });
        } else if (what === 'unlock') {
          await api.post(`/api/staff/${person.id}/unlock`);
        } else {
          const on = what === 'on';
          if (!on && !confirm(`Turn off ${person.name}'s login? Their records stay in the system.`)) return;
          await api.post(`/api/staff/${person.id}`, { active: on });
        }
        await load();
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That did not work.');
      }
    },
    [load],
  );

  if (newPassword) {
    return (
      <section className="card wide">
        <h2>Password for {newPassword.name}</h2>
        <p className="password">{newPassword.password}</p>
        <p className="muted">{newPassword.note}</p>
        <p className="muted small">
          It is shown once and nobody can look it up later. If it gets lost, give them another one.
        </p>
        <button type="button" onClick={() => setNewPassword(null)}>Done</button>
      </section>
    );
  }

  return (
    <section>
      <div className="head">
        <h1>Staff logins</h1>
        <button type="button" onClick={() => setAdding(true)}>Set up a login</button>
      </div>
      <p className="muted">
        Everyone records under their own login. Sharing one login makes the MAR chart worthless as a record.
      </p>

      {error ? <p className="error" role="alert">{error}</p> : null}

      {adding ? (
        <AddStaff
          homeId={homeId}
          onCancel={() => setAdding(false)}
          onAdded={(name, password, note) => {
            setAdding(false);
            setNewPassword({ name, password, note });
            void load();
          }}
        />
      ) : null}

      {staff === null ? <p className="muted">Loading…</p> : null}

      <ul className="list">
        {(staff ?? []).map((person) => (
          <li key={person.id} className={person.active ? '' : 'is-off'}>
            <div className="row static column">
              <div className="row-line">
                <span className="row-main">{person.name}</span>
                <span className="muted small">{person.jobTitle}</span>
              </div>
              <div className="chips">
                {person.roles.map((role) => (
                  <span className="chip" key={role}>{ROLES.find((r) => r.id === role)?.label ?? role}</span>
                ))}
                {person.agency && person.shiftEndsAt ? (
                  <span className="chip warn">Agency, ends {person.shiftEndsAt.slice(0, 5)}</span>
                ) : null}
                {person.competencyOn ? (
                  <span className="chip">Competency to {formatDate(person.competencyOn)}</span>
                ) : null}
                {!person.active ? <span className="chip off">Turned off</span> : null}
                {person.locked ? <span className="chip warn">Locked out</span> : null}
                {person.mustSetPassword ? <span className="chip">Has not chosen a password</span> : null}
              </div>
              <div className="row-actions">
                <button type="button" className="quiet" onClick={() => void act(person, 'reset')}>
                  New password
                </button>
                {person.locked ? (
                  <button type="button" className="quiet" onClick={() => void act(person, 'unlock')}>Unlock</button>
                ) : null}
                {person.active ? (
                  person.id === myId ? null : (
                    <button type="button" className="quiet" onClick={() => void act(person, 'off')}>Turn off</button>
                  )
                ) : (
                  <button type="button" className="quiet" onClick={() => void act(person, 'on')}>Turn back on</button>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function AddStaff({
  homeId,
  onAdded,
  onCancel,
}: {
  homeId: string;
  onAdded: (name: string, password: string, note: string) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [roles, setRoles] = useState<string[]>(['meds', 'view']);
  const [agency, setAgency] = useState(false);
  const [shiftEndsAt, setShiftEndsAt] = useState('20:00');
  const [competencyOn, setCompetencyOn] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const toggle = (role: string) =>
    setRoles((current) => (current.includes(role) ? current.filter((r) => r !== role) : [...current, role]));

  const submit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (!roles.length) {
        setError('Choose what this person may do.');
        return;
      }
      setBusy(true);
      setError('');
      try {
        const data = await api.post<{ password: string; message: string }>(`/api/homes/${homeId}/staff`, {
          name,
          email,
          jobTitle,
          roles,
          agency,
          shiftEndsAt: agency ? shiftEndsAt : null,
          competencyOn: competencyOn || null,
        });
        onAdded(name, data.password, data.message);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That did not work.');
      } finally {
        setBusy(false);
      }
    },
    [homeId, name, email, jobTitle, roles, agency, shiftEndsAt, competencyOn, onAdded],
  );

  return (
    <form className="card wide" onSubmit={submit}>
      <h2>Set up a login</h2>

      <label htmlFor="s-name">Name</label>
      <input id="s-name" required value={name} onChange={(e) => setName(e.target.value)} />

      <label htmlFor="s-email">Email</label>
      <input id="s-email" type="email" required autoCapitalize="off" value={email}
        onChange={(e) => setEmail(e.target.value)} />
      <p className="muted small">They sign in with this. It does not have to receive email.</p>

      <label htmlFor="s-job">Job title</label>
      <input id="s-job" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)}
        placeholder="Senior carer" />

      <fieldset>
        <legend>What they may do</legend>
        {ROLES.map((role) => (
          <label className="check" key={role.id}>
            <input type="checkbox" checked={roles.includes(role.id)} onChange={() => toggle(role.id)} />
            <span>
              <strong>{role.label}</strong>
              <span className="muted small"> {role.help}</span>
            </span>
          </label>
        ))}
      </fieldset>

      <label className="check">
        <input type="checkbox" checked={agency} onChange={(e) => setAgency(e.target.checked)} />
        <span><strong>Agency staff</strong><span className="muted small"> the login stops at the end of the shift</span></span>
      </label>

      {agency ? (
        <>
          <label htmlFor="s-shift">Shift ends at</label>
          <input id="s-shift" type="time" required value={shiftEndsAt}
            onChange={(e) => setShiftEndsAt(e.target.value)} />
        </>
      ) : null}

      <label htmlFor="s-comp">Medicines competency valid until (optional)</label>
      <input id="s-comp" type="date" value={competencyOn} onChange={(e) => setCompetencyOn(e.target.value)} />
      <p className="muted small">After this date they cannot record medicines until it is renewed.</p>

      {error ? <p className="error" role="alert">{error}</p> : null}

      <div className="row-actions">
        <button type="submit" disabled={busy}>{busy ? 'Setting up…' : 'Set up the login'}</button>
        <button type="button" className="quiet" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y?.slice(2)}`;
}
